import { useParams, Link } from "react-router-dom";
import { Container, MButton, ArrowRight, MarketingPage } from "./ui";
import { getPost, POSTS } from "./posts";
import { SITE, breadcrumb } from "./seo";

function NotFoundPost() {
  return (
    <MarketingPage
      seo={{
        title: "Article not found — WonderfulInterview",
        description: "This article doesn't exist or has moved.",
        path: "/blog",
        noindex: true,
      }}
    >
      <Container className="flex min-h-[50vh] flex-col items-center justify-center py-24 text-center">
        <h1 className="text-3xl font-bold tracking-tight text-warm-ink">Article not found</h1>
        <p className="mt-3 text-warm-muted">It may have moved or never existed.</p>
        <div className="mt-7">
          <MButton to="/blog" className="px-6 py-3">
            Back to the blog <ArrowRight />
          </MButton>
        </div>
      </Container>
    </MarketingPage>
  );
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPost(slug) : undefined;
  if (!post) return <NotFoundPost />;

  const url = `${SITE.url}/blog/${post.slug}`;
  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const articleJsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.iso,
    dateModified: post.iso,
    author: { "@type": "Organization", name: SITE.name },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: `${SITE.url}/favicon.svg` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  return (
    <MarketingPage
      seo={{
        title: `${post.title} — WonderfulInterview`,
        description: post.excerpt,
        path: `/blog/${post.slug}`,
        image: post.image,
        type: "article",
        jsonLd: [
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          articleJsonLd,
        ],
      }}
    >
      <article>
        {/* Header */}
        <Container className="pt-16 sm:pt-20">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-warm-accent">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M11 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            All articles
          </Link>
          <div className="mx-auto mt-6 max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-warm-accent/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-warm-accent">
                {post.category}
              </span>
              <span className="text-sm text-warm-muted">
                {post.date} · {post.read}
              </span>
            </div>
            <h1 className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight text-warm-ink sm:text-5xl">
              {post.title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-warm-muted">{post.excerpt}</p>
            <div className="mt-5 text-sm text-warm-muted">By {post.author}</div>
          </div>
        </Container>

        {/* Hero image */}
        <Container className="mt-10">
          <div className="mx-auto max-w-4xl overflow-hidden rounded-4xl border border-warm-border shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
            <img src={post.image} alt={post.title} className="h-[280px] w-full object-cover sm:h-[420px]" />
          </div>
        </Container>

        {/* Body */}
        <Container className="py-14">
          <div className="mx-auto max-w-2xl">
            {post.body.map((block, i) =>
              block.type === "h2" ? (
                <h2 key={i} className="mt-10 mb-3 text-2xl font-bold tracking-tight text-warm-ink">
                  {block.text}
                </h2>
              ) : (
                <p key={i} className="mb-5 text-[17px] leading-relaxed text-warm-ink/80">
                  {block.text}
                </p>
              ),
            )}
          </div>
        </Container>

        {/* CTA */}
        <Container className="pb-14">
          <div className="mx-auto max-w-2xl rounded-4xl border border-warm-border bg-warm-sand/50 p-8 text-center sm:p-10">
            <h3 className="text-2xl font-bold tracking-tight text-warm-ink">Run your first AI interview free</h3>
            <p className="mx-auto mt-2 max-w-md text-warm-muted">
              Set it up in minutes. Pay only when a candidate completes — no seats, no subscription.
            </p>
            <div className="mt-6 flex justify-center">
              <MButton to="/signup" className="px-6 py-3">
                Get started <ArrowRight />
              </MButton>
            </div>
          </div>
        </Container>
      </article>

      {/* Related */}
      <section className="border-t border-warm-border bg-warm-sand/30 py-16">
        <Container>
          <h2 className="text-2xl font-bold tracking-tight text-warm-ink">Keep reading</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group flex flex-col overflow-hidden rounded-3xl border border-warm-border bg-warm-card transition-shadow hover:shadow-[0_20px_50px_-25px_rgba(36,31,25,0.3)]"
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-warm-accent">{p.category}</span>
                  <h3 className="mt-2 text-base font-semibold leading-snug text-warm-ink">{p.title}</h3>
                  <span className="mt-3 text-xs text-warm-muted">{p.read}</span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>
    </MarketingPage>
  );
}
